import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Search,
  Sparkles,
  Heart,
  Eye,
  Star,
  FileVideo,
  ArrowRight,
  ChevronsRight,
} from "lucide-react";
import { API_BASE_URL } from '../../config';
import { cn } from '../../lib/utils';
import { getPlatformIcon } from '../../utils/platformIcons';

export interface TrendMediaMatch {
  id: string;
  filePath: string;
  thumbnailPath: string | null;
  mediaType: string;
  fileSize: number | null;
  aiMetadata: {
    videoTitle?: string;
    description?: string;
    tags?: string[];
    suggestedTexts?: { en?: string[]; es?: string[] };
  } | null;
  postStats: {
    postId: string;
    likes: number;
    views: number;
    platform: string;
    isBanger: boolean;
  } | null;
}

interface TrendMediaResponse {
  type: 'single' | 'comparison';
  matches?: TrendMediaMatch[];
  before?: TrendMediaMatch;
  after?: TrendMediaMatch;
}

interface TrendMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  trendName: string;
  onSelectMedia: (media: TrendMediaMatch | TrendMediaMatch[]) => void;
  onStartFromScratch: () => void;
}

export const TrendMediaModal: React.FC<TrendMediaModalProps> = ({
  isOpen,
  onClose,
  trendName,
  onSelectMedia,
  onStartFromScratch,
}) => {
  const [state, setState] = useState<'loading' | 'results' | 'comparison' | 'empty'>('loading');
  const [matches, setMatches] = useState<TrendMediaMatch[]>([]);
  const [comparisonPair, setComparisonPair] = useState<{ before: TrendMediaMatch; after: TrendMediaMatch } | null>(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!isOpen) return;

    setState('loading');
    setMatches([]);
    setComparisonPair(null);

    const fetchMatches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/recommendations/recommend-media-for-trend`, {
          params: { trendName },
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data as TrendMediaResponse;

        if (data.type === 'comparison' && data.before && data.after) {
          setComparisonPair({ before: data.before, after: data.after });
          setState('comparison');
        } else if (data.type === 'single' && data.matches && data.matches.length > 0) {
          setMatches(data.matches);
          setState('results');
        } else {
          setState('empty');
        }
      } catch (error) {
        console.error('[TrendMediaModal] Error fetching matches:', error);
        setState('empty');
      }
    };

    fetchMatches();
  }, [isOpen, trendName]);

  // Auto-redirect when no matches found (3s to see state for debugging)
  useEffect(() => {
    if (state === 'empty') {
      const timer = setTimeout(() => {
        onStartFromScratch();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const getThumbnailUrl = (filePath: string, w: number = 300) => {
    return `${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(filePath)}&w=${w}&token=${token}`;
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const renderMediaStats = (media: TrendMediaMatch) => {
    if (media.postStats) {
      return (
        <div className="flex items-center gap-2 mt-1">
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Heart className="h-2.5 w-2.5 text-red-400" />
            {formatNumber(media.postStats.likes)}
          </span>
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Eye className="h-2.5 w-2.5 text-primary" />
            {formatNumber(media.postStats.views)}
          </span>
          <span className="shrink-0">
            {getPlatformIcon(media.postStats.platform, 'h-2.5 w-2.5')}
          </span>
          {media.postStats.isBanger && (
            <Star className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />
          )}
        </div>
      );
    }
    return <p className="text-[10px] text-muted-foreground/60 mt-0.5 italic">Not published yet</p>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-background border-border">
        {/* Loading State */}
        {state === 'loading' && (
          <div className="py-12 text-center space-y-4">
            <div className="relative mx-auto w-16 h-16">
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20">
                <Search className="h-7 w-7 text-primary animate-pulse" />
              </div>
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Analyzing opportunity...</h3>
              <p className="text-xs text-muted-foreground mt-1">Finding the best video for this trend</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {state === 'empty' && (
          <div className="py-12 text-center space-y-4">
            <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-muted/50 border border-border">
              <FileVideo className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">No matches found</h3>
              <p className="text-xs text-muted-foreground mt-1">Opening create post...</p>
            </div>
          </div>
        )}

        {/* Comparison Pair State */}
        {state === 'comparison' && comparisonPair && (
          <>
            <DialogHeader>
              <DialogTitle className="text-sm font-black uppercase tracking-[0.15em] flex items-center gap-2">
                <div className="p-1.5 bg-primary/20 rounded-lg">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                AI suggested a pair!
              </DialogTitle>
            </DialogHeader>

            {/* Before → After preview */}
            <div className="flex items-center gap-3 justify-center py-2">
              {/* Before card */}
              <div className="flex-1 text-center space-y-2">
                <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[10px] uppercase tracking-wider">
                  Before
                </Badge>
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted border border-border/50 mx-auto">
                  <img
                    src={getThumbnailUrl(comparisonPair.before.thumbnailPath || comparisonPair.before.filePath)}
                    alt="Before"
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <p className="text-[11px] font-medium text-foreground truncate px-1">
                  {(comparisonPair.before.aiMetadata as any)?.videoTitle || 'Untitled'}
                </p>
                {renderMediaStats(comparisonPair.before)}
              </div>

              {/* Arrow */}
              <div className="shrink-0 flex flex-col items-center gap-1">
                <ChevronsRight className="h-5 w-5 text-primary" />
              </div>

              {/* After card */}
              <div className="flex-1 text-center space-y-2">
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] uppercase tracking-wider">
                  After
                </Badge>
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted border border-border/50 mx-auto">
                  <img
                    src={getThumbnailUrl(comparisonPair.after.thumbnailPath || comparisonPair.after.filePath)}
                    alt="After"
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <p className="text-[11px] font-medium text-foreground truncate px-1">
                  {(comparisonPair.after.aiMetadata as any)?.videoTitle || 'Untitled'}
                </p>
                {renderMediaStats(comparisonPair.after)}
              </div>
            </div>

            {/* Action buttons */}
            <Button
              className="w-full"
              onClick={() => onSelectMedia([comparisonPair.before, comparisonPair.after])}
            >
              Use this pair
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-[10px] text-muted-foreground uppercase tracking-widest">or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full text-xs text-muted-foreground hover:text-foreground border-border/50"
              onClick={onStartFromScratch}
            >
              Start from scratch
            </Button>
          </>
        )}

        {/* Single Select Results State */}
        {state === 'results' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-sm font-black uppercase tracking-[0.15em] flex items-center gap-2">
                <div className="p-1.5 bg-primary/20 rounded-lg">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                Found {matches.length} {matches.length === 1 ? 'opportunity' : 'opportunities'}!
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {matches.map((media, idx) => {
                const aiMeta = media.aiMetadata;
                const title = aiMeta?.videoTitle || 'Untitled video';
                const thumbPath = media.thumbnailPath || media.filePath;

                return (
                  <button
                    key={media.id}
                    onClick={() => onSelectMedia(media)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all duration-200",
                      "bg-background/50 border-border/50 hover:border-primary/30 hover:bg-muted/60",
                      "text-left group/item cursor-pointer"
                    )}
                  >
                    {/* Thumbnail */}
                    <div className="relative shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted border border-border/50">
                      <img
                        src={getThumbnailUrl(thumbPath)}
                        alt={title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      {idx === 0 && (
                        <div className="absolute top-0.5 left-0.5">
                          <Badge className="bg-primary/90 text-[8px] px-1 py-0 leading-tight">BEST</Badge>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground truncate group-hover/item:text-primary transition-colors">
                        {title}
                      </h4>

                      {media.postStats ? (
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Heart className="h-3 w-3 text-red-400" />
                            {formatNumber(media.postStats.likes)}
                          </span>
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Eye className="h-3 w-3 text-primary" />
                            {formatNumber(media.postStats.views)}
                          </span>
                          <span className="shrink-0">
                            {getPlatformIcon(media.postStats.platform, 'h-3 w-3')}
                          </span>
                          {media.postStats.isBanger && (
                            <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                          )}
                        </div>
                      ) : (
                        <p className="text-[11px] text-muted-foreground/60 mt-1 italic">Not published yet</p>
                      )}
                    </div>

                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover/item:text-primary shrink-0 transition-colors" />
                  </button>
                );
              })}
            </div>

            {/* Divider + Start from scratch */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-[10px] text-muted-foreground uppercase tracking-widest">or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full text-xs text-muted-foreground hover:text-foreground border-border/50"
              onClick={onStartFromScratch}
            >
              Start from scratch
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
